import React, { useState, useEffect } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useTranslation } from 'react-i18next';
import './LeadForm.css';

interface LeadFormProps {
  data?: unknown;
  apiBaseUrl: string;
  sessionId: string;
  sessionKey: string;
  /** Callback for SSE events - routes to ChatEngine.handleSseEvent */
  onSseEvent: (event: { event: string; data: string }) => void;
  /** Language for form labels (default: 'en') */
  language?: string;
}

export const LeadForm: React.FC<LeadFormProps> = ({
  apiBaseUrl,
  sessionId,
  sessionKey,
  onSseEvent,
  language = 'en',
}) => {
  const { t, i18n } = useTranslation();

  // Sync language when prop changes
  useEffect(() => {
    if (language && i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [contactPreference, setContactPreference] = useState<'Phone' | 'Email'>('Phone');
  const [bestTimeToContact, setBestTimeToContact] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isStreamComplete, setIsStreamComplete] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (isStreamComplete) {
      const timer = setTimeout(() => setIsDismissed(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [isStreamComplete]);

  const validatePhone = (phone: string): boolean => {
    // E.164 format validation: +[country code][number]
    const e164Regex = /^\+[1-9]\d{1,14}$/;
    return e164Regex.test(phone);
  };

  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = t('leadForm.validation.firstNameRequired');
    }

    if (!lastName.trim()) {
      newErrors.lastName = t('leadForm.validation.lastNameRequired');
    }

    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = t('leadForm.validation.phoneRequired');
    } else if (!validatePhone(phoneNumber)) {
      newErrors.phoneNumber = t('leadForm.validation.phoneInvalid');
    }

    if (!email.trim()) {
      newErrors.email = t('leadForm.validation.emailRequired');
    } else if (!validateEmail(email)) {
      newErrors.email = t('leadForm.validation.emailInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!sessionId || !sessionKey) {
      setErrors({ submit: t('leadForm.validation.submitError') });
      return;
    }

    setIsSubmitting(true);
    setIsSubmitted(true);
    setErrors({});

    try {
      await fetchEventSource(`${apiBaseUrl}/v1/sessions/${sessionId}/lead`, {
        method: 'POST',
        openWhenHidden: true,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          sessionKey,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phoneNumber: phoneNumber.trim(),
          email: email.trim(),
          contactPreference,
          bestTimeToContact: bestTimeToContact || null,
        }),
        onmessage: (event) => {
          onSseEvent({ event: event.event ?? '', data: event.data });
        },
        onerror: (err) => {
          console.error('SSE error during lead submission:', err);
          throw err;
        },
      });

      setIsStreamComplete(true);
    } catch (error) {
      console.error('Failed to submit lead info:', error);
      setIsSubmitted(false);
      setErrors({
        submit: error instanceof Error ? error.message : t('leadForm.validation.submitError'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isDismissed) {
    return null;
  }

  if (isSubmitted) {
    return (
      <div className="lead-form-container">
        <div className={`lead-form-card lead-form-submitted${isStreamComplete ? ' lead-form-done' : ''}`}>
          <div className="lead-form-submitted-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
          </div>
          <h3 className="lead-form-title">{t('leadForm.submitted.thanks')}, {firstName.trim()}!</h3>
          <p className="lead-form-description lead-form-preparing">
            {!isStreamComplete && <span className="lead-form-preparing-dot"/>}
            {isStreamComplete ? t('leadForm.submitted.done') : t('leadForm.submitted.preparing')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="lead-form-container">
      <div className="lead-form-card">
        <h3 className="lead-form-title">{t('leadForm.title')}</h3>
        <p className="lead-form-description">{t('leadForm.description')}</p>

        <form onSubmit={handleSubmit} className="lead-form">
          <div className="form-group">
            <label htmlFor="lead-firstName" className="form-label">
              {t('leadForm.labels.firstName')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="lead-firstName"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={`form-input ${errors.firstName ? 'error' : ''}`}
              placeholder={t('leadForm.placeholders.firstName')}
              disabled={isSubmitting}
            />
            {errors.firstName && <span className="error-message">{errors.firstName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="lead-lastName" className="form-label">
              {t('leadForm.labels.lastName')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="lead-lastName"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={`form-input ${errors.lastName ? 'error' : ''}`}
              placeholder={t('leadForm.placeholders.lastName')}
              disabled={isSubmitting}
            />
            {errors.lastName && <span className="error-message">{errors.lastName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="lead-phone" className="form-label">
              {t('leadForm.labels.phone')} <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="lead-phone"
              autoComplete="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className={`form-input ${errors.phoneNumber ? 'error' : ''}`}
              placeholder={t('leadForm.placeholders.phone')}
              disabled={isSubmitting}
            />
            <span className="form-hint">{t('leadForm.hints.phone')}</span>
            {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="lead-email" className="form-label">
              {t('leadForm.labels.email')} <span className="required">*</span>
            </label>
            <input
              type="email"
              id="lead-email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder={t('leadForm.placeholders.email')}
              disabled={isSubmitting}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">
              {t('leadForm.labels.contactPreference')} <span className="required">*</span>
            </label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="contactPreference"
                  value="Phone"
                  checked={contactPreference === 'Phone'}
                  onChange={(e) => setContactPreference(e.target.value as 'Phone' | 'Email')}
                  className="radio-input"
                  disabled={isSubmitting}
                />
                <span>{t('leadForm.options.phone')}</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="contactPreference"
                  value="Email"
                  checked={contactPreference === 'Email'}
                  onChange={(e) => setContactPreference(e.target.value as 'Phone' | 'Email')}
                  className="radio-input"
                  disabled={isSubmitting}
                />
                <span>{t('leadForm.options.email')}</span>
              </label>
            </div>
          </div>

          {contactPreference === 'Phone' && (
            <div className="form-group">
              <label htmlFor="lead-bestTime" className="form-label">
                {t('leadForm.labels.bestTimeToContact')}
              </label>
              <select
                id="lead-bestTime"
                value={bestTimeToContact}
                onChange={(e) => setBestTimeToContact(e.target.value)}
                className="form-input"
                disabled={isSubmitting}
              >
                <option value="">{t('leadForm.placeholders.selectTime')}</option>
                <option value="Morning (9AM-12PM)">{t('leadForm.options.morning')}</option>
                <option value="Afternoon (12PM-5PM)">{t('leadForm.options.afternoon')}</option>
                <option value="Evening (5PM-9PM)">{t('leadForm.options.evening')}</option>
              </select>
            </div>
          )}

          {errors.submit && <div className="submit-error">{errors.submit}</div>}

          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? t('leadForm.buttons.submitting') : t('leadForm.buttons.submit')}
          </button>
        </form>
      </div>
    </div>
  );
};
